import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EventIcon from "@mui/icons-material/Event";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const PartnerTripCard = ({ connection, fromCity, toCity, adults, children }) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const price = Number(connection.price || 0);
  const seats = connection.free_seats;
  const seatsLabel =
    seats == null
      ? ""
      : seats > 5
        ? "5+"
        : String(seats);

  const handleSelect = () => {
    navigate("/checkout", {
      state: {
        type: "partner",
        provider: connection.provider || "partner",
        connection,
        fromCity,
        toCity,
        adults,
        children,
      },
    });
  };

  return (
    <Card component="article" sx={{ borderRadius: 2, boxShadow: 2 }}>
      <CardContent sx={{ p: isMobile ? 2 : 2.5 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 2,
            alignItems: isMobile ? "stretch" : "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
              <Chip
                size="small"
                label={t("trips.partnerCarrier", { defaultValue: "Партнер" })}
                color="secondary"
              />
              {connection.carrier && (
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {connection.carrier}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <EventIcon fontSize="small" color="action" />
                <Typography variant="body2">{connection.date}</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <ScheduleIcon fontSize="small" color="action" />
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {connection.departure_time}
                  {connection.arrival_time ? ` → ${connection.arrival_time}` : ""}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <DirectionsBusIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary" noWrap>
                  {connection.from} → {connection.to}
                </Typography>
              </Box>
            </Box>

            {(connection.departure_station || connection.arrival_station) && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                {[connection.departure_station, connection.arrival_station]
                  .filter(Boolean)
                  .join(" → ")}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "row" : "column",
              alignItems: isMobile ? "center" : "flex-end",
              justifyContent: "space-between",
              gap: 1,
              minWidth: isMobile ? undefined : 140,
            }}
          >
            <Box sx={{ textAlign: isMobile ? "left" : "right" }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "primary.main" }}>
                {t("units.uahAmount", { amount: Math.round(price) })}
              </Typography>
              {seatsLabel && (
                <Typography variant="caption" color="text.secondary">
                  {seatsLabel}
                </Typography>
              )}
            </Box>
            <Button variant="contained" onClick={handleSelect}>
              {t("trips.select")}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PartnerTripCard;
